import { TIMELINE_EVENT_TYPES } from '@dapperlabs/core-fe';
import isEmpty from 'lodash/isEmpty';
import { FLAG_ICONS } from 'src/general/utils/flags';
import { extractValue } from 'src/general/utils/helpers';
import {
  getCarouselItems,
  getHeroImage,
  getMainVideo,
  getMomentType,
  getOwner,
  getPlayerImage,
} from 'src/general/utils/moments/momentDetails';
import { getStatistics } from 'src/general/utils/moments/stats';
import { getAbsoluteValue } from 'src/general/utils/moments/stats/matchStats';
import { getTeamData } from 'src/modules/Branding';
import { getPlayerName } from 'src/modules/searchGolazosNft';

const getCountryFlagIcon = (country) => {
  return country ? FLAG_ICONS[country.toLowerCase()] : null;
};

// bound to moment edition & plays
export const getPlayerInfo = (play: any, edition: any) => {
  return {
    countryIcon: getCountryFlagIcon(
      extractValue(edition?.play?.metadata?.player_country),
    ),
    name: getPlayerName(edition?.play?.metadata),
    playerID: play?.playerID,
    position: play?.playerPosition.toLowerCase(),
    season: extractValue(edition?.play?.metadata?.match_season),
    src: getPlayerImage(play?.playerID),
    team: extractValue(edition?.play?.metadata?.match_highlighted_team),
  };
};

export const getMomentInfo = ({
  moment,
  play,
  playDataID,
  languages,
  t,
  sessionState,
  showMobileVideos,
  aggregationData,
}) => {
  const metadata = moment?.edition?.play?.metadata;

  return {
    burnedSize: extractValue(moment?.edition?.total_burned),
    burnedSizePercent: getAbsoluteValue(
      extractValue(moment?.edition?.total_burned),
      extractValue(moment?.edition?.max_mintings),
    ),
    carouselItems: getCarouselItems(playDataID, languages, showMobileVideos),
    chartData: [
      {
        id: 'owned',
        percent: getAbsoluteValue(
          extractValue(moment?.edition?.total_minted) -
            extractValue(moment?.edition?.total_burned) -
            extractValue(aggregationData.ownedCount),
          extractValue(moment?.edition?.max_mintings),
          2,
        ),
        value:
          extractValue(moment?.edition?.total_minted) -
          extractValue(moment?.edition?.total_burned) -
          extractValue(aggregationData.ownedCount),
      },
      {
        id: 'forsale',
        percent: getAbsoluteValue(
          extractValue(aggregationData.forSaleCount),
          extractValue(moment?.edition?.max_mintings),
          3,
        ),
        value: aggregationData.forSaleCount,
      },
      {
        id: 'reserved',
        percent: getAbsoluteValue(
          extractValue(aggregationData.reservedCount),
          extractValue(moment?.edition?.max_mintings),
          2,
        ),
        value: extractValue(aggregationData.reservedCount),
      },
      {
        id: 'hidden',
        percent: getAbsoluteValue(
          extractValue(aggregationData.hiddenCount),
          extractValue(moment?.edition?.max_mintings),
          2,
        ),
        value: extractValue(aggregationData.hiddenCount),
      },
    ],
    circulationCount:
      extractValue(moment?.edition?.total_minted) -
      extractValue(moment?.edition?.total_burned),
    editionId: extractValue(moment?.edition?.id),
    editionSize: extractValue(moment?.edition?.max_mintings),
    heroImage: getHeroImage(playDataID, languages),
    moment: {
      ownerBy: getOwner(
        moment?.owner_address,
        sessionState,
        t,
        moment?.user?.username,
      ),
      serialNumber: extractValue(moment?.serial_number),
      title: getPlayerName(metadata),
      type: getMomentType(
        moment?.edition?.play?.metadata,
        moment?.edition?.set,
        t,
      ),
    },
    momentMainVideo: getMainVideo(playDataID),
    rarityType: extractValue(moment?.edition?.tier),
    summary: {
      en: play?.descriptionEN,
      es: play?.descriptionES,
    },
    totalBurned: extractValue(moment?.edition?.total_burned),
  };
};

export const getPlayInfo = (play, edition) => {
  const metadata = edition?.play?.metadata;
  const showTimeline = extractValue(metadata?.match_season) !== '2005-2006';

  return {
    awayTeam: {
      logo: getTeamData(extractValue(metadata?.match_away_team))?.logoPath,
      score: Number(extractValue(metadata?.match_away_score)),
      title: extractValue(metadata?.match_away_team),
    },
    awayTeamGoals: play?.awayTeamGoals,
    homeTeam: {
      logo: getTeamData(extractValue(metadata?.match_home_team))?.logoPath,
      score: Number(extractValue(metadata?.match_home_score)),
      title: extractValue(metadata?.match_home_team),
    },
    homeTeamGoals: play?.homeTeamGoals,
    timeline: showTimeline
      ? [
          ...(!isEmpty(play?.timeline) ? play.timeline : []),
          {
            eventType: TIMELINE_EVENT_TYPES.MOMENT,
            half: extractValue(metadata.play_half),
            min: extractValue(metadata.play_time),
            scoredBy: extractValue(metadata.player_jersey_name),
            teamName: extractValue(metadata.match_highlighted_team),
          },
        ]
      : [],
  };
};

const mapSalesHistory = (
  {
    node: {
      nft_id,
      nft: { serial_number },
      receiver: { username },
      price,
      updated_at: { block_time },
    },
  },
  digits = 2,
) => ({
  buyerUsername: username,
  id: nft_id,
  price:
    Math.round((price * Math.pow(10, digits)) / Math.pow(10, 8)) /
    Math.pow(10, digits),
  serial: serial_number,
  timestamp: block_time,
});

export const getSalesHistory = (salesHistory) => ({
  latestSales: {
    count: extractValue(salesHistory.latestSales.count),
    data: salesHistory.latestSales.data.map(mapSalesHistory),
  },
  topSales: {
    count: extractValue(salesHistory.topSales.count),
    data: salesHistory.topSales.data.map(mapSalesHistory),
  },
});

export const getMomentData = ({
  moment,
  play,
  playDataID,
  languages,
  t,
  sessionState = null,
  aggregationData,
  salesHistory,
  showMobileVideos,
}) => {
  return {
    ...getMomentInfo({
      aggregationData,
      languages,
      moment,
      play,
      playDataID,
      sessionState,
      showMobileVideos,
      t,
    }),
    ...(!isEmpty(getPlayInfo(play, moment?.edition))
      ? getPlayInfo(play, moment?.edition)
      : {}),
    ...getPlayerInfo(play, moment?.edition),
    ...getStatistics(
      extractValue(moment?.edition?.play?.metadata?.match_season),
      play,
    ),
    ...getSalesHistory(salesHistory),
  };
};
